import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type SitemapEntry } from '@nasa-gcn/remix-seo/build/types'
import { EditorContent, type HTMLContent } from '@tiptap/react'
import { format } from 'date-fns'
import { convert } from 'html-to-text'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { data, Link, type LoaderFunctionArgs, redirect } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import StackedCardsComponent
	from '#app/components/blog/stacked-cards-layout.tsx'
import MarketingImage from '#app/components/marketing/image.tsx'
import { LandingSection } from '#app/components/marketing/landing-section.tsx'
import MarketingSpacer from '#app/components/marketing/spacer.tsx'
import SplitText from '#app/components/motion/split-text.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import {
	useIntersectionObserver
} from '#app/hooks/use-intersection-observer.ts'
import { usePublicContext } from '#app/routes/($lang)+/__public-context.tsx'
import {
	useBlockEditor
} from '#app/routes/admin+/blog+/posts+/editor/user-block-editor.js'
import { useMainStore } from '#app/stores/main'
import { getLatestPosts } from '#app/utils/blog.server.ts'
import { BUSINESS_NAME } from '#app/utils/business.ts'
import { cachifiedWithMetrics } from '#app/utils/cache-metrics.server.ts'
import { BASE_URL } from '#app/utils/config.ts'
import { prisma } from '#app/utils/db.server.js'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import {
	getOGLocales,
	i18n,
	type ISupportedLanguage,
	LocalizedLink,
} from '#app/utils/i18n.tsx'
import { getPostImgSrc } from '#app/utils/misc.tsx'
import { createPerformanceTimer } from '#app/utils/timing.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import routes from '#routes/index.tsx'
import { type Route } from './+types/$slug.tsx'

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => {
		const posts = await prisma.post.findMany({
			where: {
				published: true,
			},
			orderBy: {
				publishedAt: 'desc',
			},
			include: {
				translations: true,
			},
		})

		const routes = new Set<SitemapEntry>()

		i18n.supportedLngs.forEach((lang) => {
			posts.forEach((post) => {
				const translation = post.translations.find(
					(t) => t.languageCode === lang,
				)
				if (!translation) return

				routes.add({
					route: `/${lang}/blog/${translation.slug}`,
					priority: 0.7,
					lastmod: post.updatedAt.toISOString(),
				})
			})
		})

		return Array.from(routes)
	}),
}

export async function loader({ params }: LoaderFunctionArgs) {
	const timer = createPerformanceTimer()
	timer.start('total', 'Total loader execution')

	let { slug, lang } = params

	if (!lang) {
		lang = i18n.fallbackLng
	}

	if (!slug) {
		throw new Response('Not found', { status: 404 })
	}

	// Create cache key based on slug and language
	const cacheKey = `magazine-article:${slug}:${lang}`

	// Fetch post data with caching (30 min TTL)
	const { post, categories, lastestArticles } = await cachifiedWithMetrics({
		key: cacheKey,
		ttl: 1000 * 60 * 30, // 30 minutes
		route: 'magazine.detail',
		async getFreshValue() {
			timer.start('db-post', 'Fetch post from database')
			let post = await prisma.post.findFirst({
				where: {
					published: true,
					removedAt: null,
					translations: {
						some: {
							slug,
							languageCode: lang,
						},
					},
				},
				include: {
					images: {
						where: {
							type: 'cover',
						},
						take: 1,
						select: {
							id: true,
							// Only select the ID - blob will be loaded on-demand via resource route
							// This drastically improves performance by not loading image data (1-5 MB per image)
						},
					},
					categories: {
						select: {
							id: true,
							translations: {
								where: {
									languageCode: lang,
								},
							},
						},
					},
					translations: {
						where: {
							languageCode: lang,
							slug,
						},
						take: 1,
					},
				},
			})

			timer.end('db-post')

			if (!post) {
				// Si no se ha encontrado la combinación de slug y lang, es posible que se haya cambiado de idioma. Buscamos el slug en cualquier idioma, cogemos el post id y redirigimos a la nueva url con el idioma correcto.
				const post = await prisma.post.findFirst({
					where: {
						published: true,
						removedAt: null,
						translations: {
							some: {
								slug,
							},
						},
					},
					include: {
						images: {
							where: {
								type: 'cover',
							},
							take: 1,
							select: {
								id: true,
								// Only select the ID - blob will be loaded on-demand via resource route
								// This drastically improves performance by not loading image data (1-5 MB per image)
							},
						},
						categories: {
							select: {
								id: true,
								translations: {
									where: {
										languageCode: lang,
									},
								},
							},
						},
						translations: {
							where: {
								languageCode: lang,
							},
							take: 1,
						},
					},
				})

				if (post && post.translations[0]?.slug) {
					throw redirect(
						routes.public.blog.post.route({
							slug: post.translations[0].slug,
						}),
						{
							status: 307,
						},
					)
				}

				throw redirect(routes.public.blog.index.route({}), {
					status: 301,
				})
			}

			timer.start('db-latest', 'Fetch latest articles')
			const lastestArticles = await getLatestPosts({
				locale: lang as ISupportedLanguage,
				exclude: [post.id],
			})
			timer.end('db-latest')

			return {
				post: {
					data: {
						...post,
						cover: post.images[0],
						translations: undefined,
					},
					content: {
						...post.translations[0],
					},
				},
				categories: post.categories.map((cat) => ({
					...cat.translations[0],
					categoryId: cat.id,
				})),
				lastestArticles,
			}
		},
	})

	timer.end('total')

	return data(
		{
			post,
			categories,
			locale: lang,
			lastestArticles,
		},
		{
			headers: {
				'Server-Timing': timer.toServerTimingHeader(),
			},
		},
	)
}

export const meta = ({ data }: Route.MetaArgs) => {
	const title = `${data.post.content.title} - ${BUSINESS_NAME}`
	const content = convert(data.post.content.content!)

	return [
		{
			title: title,
		},
		{
			name: 'og:title',
			content: title,
		},
		{
			description: `${content.split('\n')[0]?.substring(0, 155)}...`,
		},
		{
			name: 'og:type',
			content: 'website',
		},
		{
			name: 'og:image',
			content: `${BASE_URL}${getPostImgSrc(data.post.data.cover?.id!)}`,
		},
		{
			name: 'og:image:alt',
			content: title,
		},
		...getOGLocales(data.locale as ISupportedLanguage),
		{
			name: 'og:site_name',
			content: BUSINESS_NAME,
		},
	]
}

export const blogContentClass =
	'prose prose-neutral prose-sm xl:prose-base  w-full prose-a:text-primary prose-hr:my-0 prose-blockquote:border-0 prose-blockquote:m-0 prose-blockquote:p-0 text-justify mx-auto prose-img:my-0'

export default function BlogPost({ loaderData }: Route.ComponentProps) {
	const { t } = useTranslation('magazine')
	const user = useOptionalUser()
	const { editor } = useBlockEditor({
		content: loaderData?.post.content.content,
		editable: false,
	})
	const changeBackgroundColor = useMainStore.use.changeBackgroundColor()

	const {
		navigation: { route },
	} = usePublicContext()

	useEffect(() => {
		route.setContent([
			{
				label: `Magazine.`,
				to: routes.public.blog.index.route({}),
			},
		])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const { ref } = useIntersectionObserver({
		threshold: 0.22,
		onChange: (isIntersecting) => {
			if (isIntersecting) {
				changeBackgroundColor(extendedTheme.colors.background)
			}
		},
	})

	useEffect(() => {
		editor?.commands.setContent(loaderData?.post.content.content as HTMLContent)
	}, [editor?.commands, loaderData?.post.content.content])

	const PostCategories = () => {
		return (
			<span className={'inline-flex gap-2'}>
				{categories.map((c) =>
					c?.name ? (
						<LocalizedLink
							key={c.id}
							to={`${routes.public.blog.index.route({})}?category=${c.categoryId}`}
						>
							{c.name}
						</LocalizedLink>
					) : null,
				)}
			</span>
		)
	}

	if (!loaderData) return null

	const { post, categories, locale, lastestArticles } = loaderData

	return (
		<>
			<MarketingSpacer side="top" />

			<article>
				<div className={'container grid grid-cols-1 gap-[5vw] md:grid-cols-2'}>
					<div className={'flex flex-col gap-sm lg:gap-lg'}>
						<Text className={'lowercase'} size={'large'} weight={'extralight'}>
							/{' '}
							<LocalizedLink to={routes.public.blog.index.route({})}>
								magazine
							</LocalizedLink>{' '}
							/ <PostCategories />
						</Text>

						{post.data.cover && (
							<MarketingImage src={getPostImgSrc(post.data.cover.id)} />
						)}
					</div>
					<div className={'flex flex-col gap-sm self-center lg:gap-lg'}>
						<Text className={'lowercase'} size={'large'} weight={'extralight'}>
							/ <PostCategories /> /
						</Text>
						<Text
							as={'div'}
							weight={'light'}
							type={'serif'}
							size={'h3'}
							className={'max-w-lg'}
						>
							<SplitText element={'words'} as={'h1'}>
								{post.content.title}
							</SplitText>
						</Text>
						{post.data.publishedAt && (
							<Text
								as={'p'}
								weight={'extralight'}
								size={'p'}
								className={'tracking-widest'}
							>
								{format(post.data.publishedAt, 'dd/MM/yyyy')}
							</Text>
						)}

						{user && user.roles.some((role) => role.name === 'admin') && (
							<Button asChild variant={'outline'} className={'mt-4 self-start'}>
								<Link
									to={routes.admin.blog.posts.edit.route({
										id: post.data?.id,
										locale,
									})}
								>
									<Icon name={'lucide-pencil'}>Editar este artículo</Icon>
								</Link>
							</Button>
						)}
					</div>
				</div>

				<MarketingSpacer side={'bottom'} />

				<div className={blogContentClass}>
					<EditorContent
						id="article"
						editor={editor}
						className="container min-w-full flex-1 overflow-y-auto"
					/>
				</div>
			</article>

			<div ref={ref}>
				<MarketingSpacer side="bottom" />
			</div>

			<LandingSection
				backgroundColor={extendedTheme.colors.brand.mist.DEFAULT}
				className={'flex flex-col gap-[5vh] py-[10vh]'}
			>
				<div className={'container mx-auto flex max-w-[1920px]'}>
					<div className={'mx-0 max-w-lg'}>
						<Text
							as={'div'}
							size={'pageTitle'}
							type={'serif'}
							weight={'extralight'}
							className={'uppercase'}
						>
							<SplitText as={'h2'} element={'chars'}>
								{t('article.lastArticles')}
							</SplitText>
						</Text>
					</div>
				</div>

				<StackedCardsComponent
					className={'container'}
					cards={lastestArticles.map((article) => ({
						title: article.translations[0]!.title,
						description: article.translations[0]!.content!,
						slug: article.translations[0]!.slug,
						image: getPostImgSrc(article.images[0]!.id),
						link: routes.public.blog.post.route({
							slug: article.translations[0]!.slug,
						}),
					}))}
				/>
			</LandingSection>
		</>
	)
}
